@if (groupPage(); as groupPage) {
  <div [class]="pageScrollClass()">
    @if (groupService.reordering) {
      <div
        class="fixed inset-x-0 top-0 z-30 border-b border-border-on-header bg-surface-header px-2 py-2 text-text-on-header shadow-overlay"
      >
        <!-- One row at every width; below `sm` the totals and buttons tighten so both fit on a 360px phone.
             The totals card matches the button height (min-h-ui-button). -->
        <div class="mx-auto flex max-w-content items-center justify-between gap-1.5 sm:gap-4">
          <div
            class="grid min-h-ui-button min-w-0 flex-1 content-center grid-cols-[0.95fr_1.1fr_0.95fr] gap-1.5 rounded-xl border border-border-on-header bg-surface-on-header-active px-2.5 py-0.5 text-text-on-header backdrop-blur sm:max-w-[520px] sm:grid-cols-[0.9fr_1fr_1.2fr] sm:gap-2 sm:px-4"
          >
            <div class="min-w-0">
              <div class="text-[10px] font-semibold uppercase tracking-[0.04em] text-text-on-header-muted sm:tracking-[0.08em]">Income</div>
              <div class="mt-0.5 truncate text-sm font-bold tabular-nums sm:text-base">
                {{ getEditIncome(groupPage) | currency: 'USD' : 'symbol' : '1.0-0' }}
              </div>
            </div>
            <div class="min-w-0 pl-1.5 sm:pl-4">
              <div class="text-[10px] font-semibold uppercase tracking-[0.04em] text-text-on-header-muted sm:tracking-[0.08em]">Expenses</div>
              <div class="mt-0.5 truncate text-sm font-bold tabular-nums sm:text-base">
                {{ getEditExpenses(groupPage) | currency: 'USD' : 'symbol' : '1.0-0' }}
              </div>
            </div>
            <div class="min-w-0 pl-1.5 sm:pl-4">
              <div
                class="text-[10px] font-semibold uppercase tracking-[0.02em] whitespace-nowrap sm:tracking-[0.06em]"
                [class.text-status-danger]="isOverBudget(groupPage)"
                [class.text-text-on-header-muted]="!isOverBudget(groupPage)"
              >
                <span class="sm:hidden">{{ getBudgetStatusShortLabel(groupPage) }}</span>
                <span class="max-sm:hidden">{{ getBudgetStatusLabel(groupPage) }}</span>
              </div>
              <div
                class="bulk-edit-budget-status-amount mt-0.5 truncate text-sm font-bold tabular-nums sm:text-base text-text-on-header"
              >
                {{
                  (isOverBudget(groupPage)
                    ? getEditExpenses(groupPage) - getEditIncome(groupPage)
                    : getEditNet(groupPage)
                  ) | currency: 'USD' : 'symbol' : getBudgetStatusDigits(groupPage)
                }}
              </div>
            </div>
          </div>
          <div class="flex shrink-0 items-center gap-1 sm:gap-2">
            <button
              appUiButton
              type="button"
              appearance="ghost"
              surface="header"
              class="bulk-edit-cancel-btn !px-2.5 sm:!px-4"
              [disabled]="saving()"
              (click)="cancelEditing()"
            >
              Cancel
            </button>
            <app-ui-save-button
              class="bulk-edit-save-btn sm:min-w-[5.5rem]"
              tone="onHeader"
              compact
              [state]="saving() ? 'saving' : 'ready'"
              (click)="doneReordering()"
            />
          </div>
        </div>
      </div>
    }
    @if (groupPage) {
      <!-- Inert and dimmed while the previous month stands in for one that is still loading. -->
      <div
        class="relative mx-auto max-w-content px-2 pb-8 pt-4 transition-opacity"
        [class.opacity-60]="showingPreviousMonth()"
        [attr.inert]="showingPreviousMonth() ? '' : null"
        [attr.aria-busy]="showingPreviousMonth() ? 'true' : null"
      >
        @if (groupService.reordering && saveError(); as saveError) {
          <div appUiAlert tone="destructive" class="bulk-edit-save-error mb-4">
            <span appUiAlertIcon aria-hidden="true">
              <app-ui-icon name="info" />
            </span>
            <h2 appUiAlertTitle>Couldn't save budgets</h2>
            <p appUiAlertDescription>{{ saveError }}</p>
          </div>
        }
        @if (updateAvailable()) {
          <div appUiAlert class="categories-page-update mb-4 !border-brand-primary/25 !bg-surface-tint !py-2.5 !pr-28">
            <span appUiAlertIcon class="!text-brand-primary" aria-hidden="true">
              <app-ui-icon name="refresh" />
            </span>
            <h2 appUiAlertTitle class="!text-sm !font-extrabold !text-text-title">Update available</h2>
            <p appUiAlertDescription class="!text-xs !text-text-muted">Refresh to load the latest Budgets app.</p>
            <button
              appUiButton
              appUiAlertAction
              appearance="solid"
              tone="primary"
              type="button"
              class="!h-8 !min-h-8 !rounded-full !px-4 !text-xs"
              (click)="updateApp()"
            >
              Refresh
            </button>
          </div>
        }
        @if (!groupService.reordering) {
          <app-categories-page-summary
            class="mb-4 block"
            [groupPage]="groupPage"
            [spendingTrend]="spendingTrend()"
          />
        }
        @if (!groupService.reordering) {
          <div class="mb-4 mt-6">
            @if (!isSearching()) {
              <div class="flex items-center justify-end gap-1">
                <button
                  appUiButton
                  appearance="ghost"
                  tone="neutral"
                  type="button"
                  class="categories-page-expand-toggle !h-9 !px-2.5 !text-brand-primary"
                  (click)="categoriesList()?.toggleAll()"
                >
                  {{ categoriesList()?.allRenderedExpanded() ? 'Collapse all' : 'Expand all' }}
                </button>
                <button
                  appUiButton
                  appearance="ghost"
                  tone="neutral"
                  type="button"
                  class="categories-page-edit-toggle !h-9 !px-4"
                  (click)="openBudgetEditing()"
                >
                  Edit
                </button>
                <button
                  appUiButton
                  appearance="ghost"
                  [tone]="hasActiveBudgetFilter() ? 'accent' : 'neutral'"
                  type="button"
                  class="categories-page-filter-toggle !h-9 !w-9 !p-0 {{
                    hasActiveBudgetFilter() ? '!bg-surface-tint !text-brand-primary !ring-1 !ring-brand-primary/30' : ''
                  }}"
                  aria-label="Filter budgets"
                  [appUiDropdownMenuTrigger]="budgetFiltersMenu"
                  align="end"
                  side="bottom"
                >
                  <ng-icon hlm size="sm" name="lucideListFilter" />
                </button>
                <app-ui-icon-button
                  class="categories-page-search-toggle"
                  icon="search"
                  tone="neutral"
                  ariaLabel="Search budgets"
                  (click)="openSearch()"
                />
                <ng-template #budgetFiltersMenu>
                  <div appUiDropdownMenu class="min-w-56" aria-label="Budget filters">
                    <button
                      appUiDropdownMenuItem
                      type="button"
                      class="categories-page-only-over-budget w-full justify-between"
                      [attr.aria-checked]="onlyOverBudget()"
                      role="menuitemcheckbox"
                      (click)="toggleOnlyOverBudget()"
                    >
                      Only over budget
                      @if (onlyOverBudget()) {
                        <app-ui-icon name="check" class="h-6 w-6 text-brand-primary" />
                      }
                    </button>
                    <button
                      appUiDropdownMenuItem
                      type="button"
                      class="categories-page-show-transfers w-full justify-between"
                      [attr.aria-checked]="showTransfers()"
                      role="menuitemcheckbox"
                      (click)="toggleShowTransfers()"
                    >
                      Show transfers
                      @if (showTransfers()) {
                        <app-ui-icon name="check" class="h-6 w-6 text-brand-primary" />
                      }
                    </button>
                  </div>
                </ng-template>
              </div>
            } @else {
              <div class="relative w-full">
                <span
                  class="pointer-events-none absolute left-4 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center text-text-muted"
                  aria-hidden="true"
                >
                  <app-ui-icon name="search" />
                </span>
                <input
                  id="category-page-search-input"
                  appUiInput
                  name="budgetSearch"
                  type="text"
                  placeholder="Search budgets"
                  aria-label="Search budgets"
                  [ngModel]="groupSearch()"
                  (ngModelChange)="setCategorySearch($event)"
                  [autofocus]="true"
                  autocomplete="off"
                  class="categories-page-search-input w-full pl-10 pr-11"
                />
                <button
                  appUiButton
                  appearance="ghost"
                  tone="neutral"
                  type="button"
                  class="categories-page-search-clear absolute right-1 top-1/2 !min-h-9 !w-9 -translate-y-1/2 !p-0"
                  aria-label="Clear search"
                  (click)="clearSearch()"
                >
                  <span class="inline-flex h-6 w-6 items-center justify-center text-text-muted" aria-hidden="true">
                    <app-ui-icon name="close" />
                  </span>
                </button>
              </div>
            }
          </div>
        }
        @if (groupService.reordering) {
          <div class="mb-4 mt-6">
            <h2 class="m-0 text-base font-bold text-text-title">Edit budgets</h2>
            <p class="mt-1 text-sm text-text-muted">
              Update groups, monthly amounts, and drag to reorder. New budgets can pick a type before saving.
            </p>
          </div>
        }
        <!-- Inert while saving: the request is already built, so later edits would be dropped on exit. -->
        <app-categories-list
          class="block"
          [attr.inert]="saving() ? '' : null"
          [attr.aria-busy]="saving() ? 'true' : null"
          [reordering]="groupService.reordering"
          [monthYear]="currentMonthYear()"
          [categories]="groupService.reordering ? categoriesWithoutTransfers() : displayedCategories()"
        />
        @if (!groupService.reordering && showTransfers()) {
          @if (transfersBucket(); as transfersBucket) {
            <!-- -mt-16 offsets the categories list's built-in mb-20 page-bottom margin so the card sits with the groups. -->
            <app-transfers-card
              class="-mt-16 mb-6 block"
              [bucket]="transfersBucket"
              [transactionCount]="transfersTransactionCount()"
              (opened)="openTransfersDrawer($event)"
            />
          }
        }
      </div>
    }
  </div>
} @else {
  @if (loading()) {
    <div class="mx-auto flex h-full max-w-content items-center justify-center px-6 py-10 text-sm text-text-muted">
      Loading budgets…
    </div>
  }
}
